import * as Sentry from "@sentry/react";
import type { Table } from "@tanstack/react-table";
import { endOfDay } from "date-fns";
import { ArrowUpDown, RefreshCcw } from "lucide-react";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { LineItem } from "@/apis/gql/types";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useTenant } from "@/providers/session";

import {
  LINE_ITEMS_QUERY,
  useGetAllLineItems,
} from "@/queries/line-items/fetch";
import { useUpdateManyLineItems } from "@/queries/line-items/update";

import { useInvoiceReviewContext } from "../ReviewProvider";

import { ValidationErrorMessage } from "@/components/shared/form/shared/form-validation";
import { useDeleteLineItems } from "@/queries/line-items/delete";
import { LineItemTable } from "./splitInvoiceTable/LineItemTable";
import { ArchivedLineItemTable as ArchivedLineItemTableComponent } from "./splitInvoiceTable/archived-line-item-table";
import { LineItemTableInstanceProvider } from "./splitInvoiceTable/table-instance-provider";

export enum InvoiceModes {
  Netto = "Netto",
  Brutto = "Brutto",
}

type ActiveLineItemsTableProps = {
  invoiceMode: InvoiceModes;
};

const ActiveLineItemsTable = forwardRef(
  ({ invoiceMode }: ActiveLineItemsTableProps, ref) => {
    const { invoiceReviewState } = useInvoiceReviewContext();
    const { invoiceData } = invoiceReviewState;
    const { hasCostCenters } = useTenant();

    return (
      <LineItemTableInstanceProvider
        ref={ref}
        data={invoiceData?.lineItems ?? []}
        invoiceMode={invoiceMode}
        columnVisibility={{
          costCenterId: !!hasCostCenters,
        }}
      >
        <div>
          {Boolean(invoiceData?.lineItems.length) && (
            <ValidationErrorMessage name="lineItems.accountIds" />
          )}
          <LineItemTable invoiceMode={invoiceMode} />
        </div>
      </LineItemTableInstanceProvider>
    );
  },
);

type ArchivedLineItemsTableProps = {
  invoiceMode: InvoiceModes;
};

const ArchivedLineItemsTable = forwardRef(
  ({ invoiceMode }: ArchivedLineItemsTableProps, ref) => {
    const { hasCostCenters } = useTenant();
    const { invoiceReviewState } = useInvoiceReviewContext();
    const { invoiceData } = invoiceReviewState;

    // APOLLO IS STUPID if you use new Date() directly in the query the query is refetch infinitely
    const now = useMemo(() => endOfDay(new Date()), []);

    const { lineItems } = useGetAllLineItems(
      invoiceData?.invoiceId!,
      {
        createdBy: "extraction",
        deletedAt: {
          value: now,
          op: "lte",
        },
      },
      { paranoid: false },
    );

    return (
      <LineItemTableInstanceProvider
        ref={ref}
        data={lineItems ?? []}
        readOnly
        invoiceMode={invoiceMode}
        columnVisibility={{
          costCenterId: !!hasCostCenters,
        }}
      >
        <ArchivedLineItemTableComponent />
      </LineItemTableInstanceProvider>
    );
  },
);

type Props = {
  tableMode: "active" | "archived";
};

export function SplitInvoiceForm({ tableMode }: Props) {
  const { invoiceReviewState } = useInvoiceReviewContext();
  const { invoiceData } = invoiceReviewState;

  const [invoiceMode, setInvoiceMode] = React.useState<InvoiceModes>();

  useEffect(() => {
    if (invoiceData?.isNettoInvoice !== undefined) {
      setInvoiceMode(
        invoiceData?.isNettoInvoice ? InvoiceModes.Netto : InvoiceModes.Brutto,
      );
    }
  }, [invoiceData?.isNettoInvoice]);

  if (!invoiceMode) {
    return null;
  }

  if (tableMode === "active") {
    return <ActiveLineItemsTable invoiceMode={invoiceMode} />;
  }

  if (tableMode === "archived") {
    return <ArchivedLineItemsTable invoiceMode={invoiceMode} />;
  }

  return null;
}

export const AllLineItemsTable = () => {
  const { invoiceReviewState } = useInvoiceReviewContext();
  const { invoiceData } = invoiceReviewState;
  const { tenantId } = useTenant();
  const activeTableRef = useRef<Table<{ lineItem: LineItem }>>(null);
  const archivedTableRef = useRef<Table<{ lineItem: LineItem }>>(null);

  const [invoiceMode, setInvoiceMode] = React.useState<InvoiceModes>();

  const updateLineItems = useUpdateManyLineItems({
    refetchQueries: [LINE_ITEMS_QUERY],
  });
  const deleteLineItems = useDeleteLineItems(
    tenantId,
    invoiceData?.invoiceId!,
    {
      refetchQueries: [LINE_ITEMS_QUERY],
    },
  );

  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const handleSwapTables = async () => {
    const archiveTable = archivedTableRef.current;
    const activeTable = activeTableRef.current;

    if (archiveTable && activeTable) {
      const toastId = toast.loading(t("generic_loading"));
      setLoading(true);

      const archiveLineItemIds = archiveTable
        .getRowModel()
        .rows.map((row) => row.original.lineItem.lineItemId);
      const activeLineItemIds = activeTable
        .getRowModel()
        .rows.map((row) => row.original.lineItem.lineItemId);

      try {
        await deleteLineItems(activeLineItemIds, invoiceData?.invoiceId);
        await updateLineItems(archiveLineItemIds, { deletedAt: null });

        toast.dismiss(toastId);
      } catch (error) {
        Sentry.captureException(error);
        toast.error(t("generic_error"), { id: toastId, richColors: true });
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (invoiceData?.isNettoInvoice !== undefined) {
      setInvoiceMode(
        invoiceData?.isNettoInvoice ? InvoiceModes.Netto : InvoiceModes.Brutto,
      );
    }
  }, [invoiceData?.isNettoInvoice]);

  if (!invoiceMode) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 mb-4">
      <ActiveLineItemsTable ref={activeTableRef} invoiceMode={invoiceMode} />
      <div className="flex flex-row gap-2 items-center">
        <Separator className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSwapTables}
          disabled={loading}
        >
          {loading ? (
            <RefreshCcw className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
        <Separator className="flex-1" />
      </div>
      <div>
        <small className="text-sm font-medium leading-none">
          Archived line items
        </small>
        <div className="opacity-50">
          <ArchivedLineItemsTable
            ref={archivedTableRef}
            invoiceMode={invoiceMode}
          />
        </div>
      </div>
    </div>
  );
};
